import * as yup from 'yup';

/**
 * =========================
 * COMMON NUMBER TRANSFORM
 * =========================
 * - "" / null  -> undefined
 * - Required field  -> trigger .required()
 * - Optional field  -> skip validation
 */
const numberTransform = (value, originalValue) =>
  originalValue === '' || originalValue === null
    ? undefined
    : value;

/* ======================================================
 * LED LIGHTING
 * ====================================================== */
export const createLedLightingSchema = (t) =>
  yup.object().shape({
    ledWattage: yup
      .number()
      .transform(numberTransform)
      .typeError(t('Must be a number'))
      .required(t('This is a required field'))
      .positive(t('Must be greater than 0')),

    fixtures: yup
      .number()
      .transform(numberTransform)
      .typeError(t('Must be a number'))
      .required(t('This is a required field'))
      .integer(t('Must be an integer'))
      .positive(t('Must be greater than 0')),

    operatingHours: yup
      .number()
      .transform(numberTransform)
      .typeError(t('Must be a number'))
      .required(t('This is a required field'))
      .min(1, t('Must be greater than 0'))
      .max(8760, t('Maximum 8760 hours per year')),

    upgradeCost: yup
      .number()
      .transform(numberTransform)
      .typeError(t('Must be a number'))
      .required(t('This is a required field'))
      .min(0, t('Must be zero or greater')),

    tariffRate: yup
      .number()
      .transform(numberTransform)
      .typeError(t('Must be a number'))
      .required(t('This is a required field'))
      .min(0, t('Must be zero or greater')),
  });

/* ======================================================
 * ROOFTOP SOLAR
 * ====================================================== */
export const createRooftopSolarSchema = (t) =>
  yup.object().shape({
    capacity: yup
      .number()
      .transform(numberTransform)
      .typeError(t('Must be a number'))
      .required(t('This is a required field'))
      .positive(t('Must be greater than 0')),

    capex: yup
      .number()
      .transform(numberTransform)
      .typeError(t('Must be a number'))
      .required(t('This is a required field'))
      .min(0, t('Must be zero or greater')),

    tariff: yup
      .number()
      .transform(numberTransform)
      .typeError(t('Must be a number'))
      .required(t('This is a required field'))
      .min(0, t('Must be zero or greater')),

    panelEfficiency: yup
      .number()
      .transform(numberTransform)
      .typeError(t('Must be a number'))
      .required(t('This is a required field'))
      .min(1, t('Must be at least 1%'))
      .max(100, t('Must be 100% or less')),

    annualGeneration: yup
      .number()
      .transform(numberTransform)
      .typeError(t('Must be a number'))
      .required(t('This is a required field'))
      .min(0, t('Must be at least zero')),

    inverterEfficiency: yup
      .number()
      .transform(numberTransform)
      .typeError(t('Must be a number'))
      .required(t('This is a required field'))
      .min(1, t('Must be at least 1%'))
      .max(100, t('Must be 100% or less')),
  });

/* ======================================================
 * HVAC OPTIMIZATION
 * ====================================================== */
export const createHvacSchema = (t) =>
  yup.object().shape({
    reductionPercent: yup
      .number()
      .transform(numberTransform)
      .typeError(t('Must be a number'))
      .required(t('This is a required field'))
      .min(1, t('Must be at least 1%'))
      .max(100, t('Must be 100% or less')),

    investmentCost: yup
      .number()
      .transform(numberTransform)
      .typeError(t('Must be a number'))
      .required(t('This is a required field'))
      .min(0, t('Must be zero or greater')),

    tariffRate: yup
      .number()
      .transform(numberTransform)
      .typeError(t('Must be a number'))
      .required(t('This is a required field'))
      .min(0, t('Must be zero or greater')),
  });

/* ======================================================
 * TEMPERATURE REDUCTION (1°C)
 * ====================================================== */
export const createTemperatureReductionSchema = (t) =>
  yup.object().shape({
    efficiencyGain: yup
      .number()
      .transform(numberTransform)
      .typeError(t('Must be a number'))
      .required(t('This is a required field'))
      .min(1, t('Must be at least 1%'))
      .max(20, t('Unusually high for 1°C change')),
  });

/* ======================================================
 * GREEN TARIFF / PPA (OPTIONAL)
 * ====================================================== */
export const createGreenTariffPpaSchema = (t) =>
  yup.object().shape({
    greenTariffPremium: yup
      .number()
      .transform(numberTransform)
      .typeError(t('Must be a number'))
      .min(0, t('Must be zero or greater')),
    currentTariffRate: yup
      .number()
      .transform(numberTransform)
      .typeError(t('Must be a number'))
      .min(0, t('Must be zero or greater')),
  });

/* ======================================================
 * FLEET ELECTRIFICATION
 * ====================================================== */
export const createFleetElectrificationSchema = (t) =>
  yup.object().shape({
    evEfficiency: yup
      .number()
      .transform(numberTransform)
      .typeError(t('Must be a number'))
      .required(t('This is a required field'))
      .min(0.05, t('Too low for EV efficiency'))
      .max(1, t('Unrealistically high value')),

    evCapex: yup
      .number()
      .transform(numberTransform)
      .typeError(t('Must be a number'))
      .required(t('This is a required field'))
      .min(0, t('Must be zero or greater')),

    chargingCost: yup
      .number()
      .transform(numberTransform)
      .typeError(t('Must be a number'))
      .required(t('This is a required field'))
      .min(0, t('Must be zero or greater')),

    fuelPrice: yup
      .number()
      .transform(numberTransform)
      .typeError(t('Must be a number'))
      .required(t('This is a required field'))
      .min(0, t('Must be zero or greater')),

    conversionFactor: yup
      .number()
      .transform(numberTransform)
      .typeError(t('Must be a number'))
      .required(t('This is a required field'))
      .moreThan(0, t('Must be greater than 0')),

    evEmissionFactor: yup
      .number()
      .transform(numberTransform)
      .typeError(t('Must be a number'))
      .required(t('This is a required field'))
      .moreThan(0, t('Must be greater than 0')),
  });